import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'
import IconifyIcon from '../../components/wrapper/IconifyIcon'
import { useAuth } from '../../contexts/AuthContext'
import { API_BASE_URL } from '../../config/api'
import '../oracle/Oracle.css'
import './OracleLive.css'

const STARTERS = [
  { icon: 'solar:chart-2-bold-duotone', title: 'Ler o jogo', prompt: 'Quem está a dominar o jogo neste momento e quais são as estatísticas que sustentam essa leitura?' },
  { icon: 'solar:football-bold-duotone', title: 'Ver acontecimentos', prompt: 'Resume os principais acontecimentos deste jogo até agora.' },
  { icon: 'solar:target-bold-duotone', title: 'Analisar remates', prompt: 'Compara os remates, as oportunidades e o xG das duas equipas.' },
  { icon: 'solar:graph-up-bold-duotone', title: 'Consultar mercados', prompt: 'Que odds estão disponíveis agora e o que mostram, sem fazer recomendações?' },
]

const TOOL_LABELS = {
  get_live_match_state: 'A atualizar o estado do jogo',
  get_live_match_detail: 'A consultar as estatísticas',
  get_live_incidents: 'A consultar os acontecimentos',
  get_live_shotmap: 'A analisar remates e xG',
  get_live_odds: 'A comparar as odds live',
  live_data: 'A atualizar os dados live',
}

const parseSseStream = async (stream, onEvent) => {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  const parseBlock = (block) => {
    let eventName = 'message'
    const dataLines = []
    block.split('\n').forEach((line) => {
      if (line.startsWith('event:')) eventName = line.slice(6).trim()
      if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart())
    })
    if (!dataLines.length) return true
    return onEvent(eventName, JSON.parse(dataLines.join('\n'))) !== false
  }

  while (true) {
    const { done, value } = await reader.read()
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done }).replace(/\r\n/g, '\n')
    let boundary = buffer.indexOf('\n\n')
    while (boundary !== -1) {
      const keepReading = parseBlock(buffer.slice(0, boundary))
      buffer = buffer.slice(boundary + 2)
      if (!keepReading) {
        await reader.cancel()
        return
      }
      boundary = buffer.indexOf('\n\n')
    }
    if (done) break
  }
  if (buffer.trim()) parseBlock(buffer)
}

const makeMessage = (role, text, extra = {}) => ({ id: `${role}-${Date.now()}-${Math.random()}`, role, text, ...extra })
const MarkdownLink = ({ href = '', children }) => {
  if (/^\//.test(href)) return <a href={href}>{children}</a>
  if (!/^https?:\/\//i.test(href)) return <span>{children}</span>
  return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
}

const minuteLabel = (match) => {
  if (match?.minute == null || match.minute === '') return match?.period || 'LIVE'
  const minute = String(match.minute)
  return /^\d+$/.test(minute) ? `${minute}'` : minute
}

const OracleLive = ({ initialMatchKey = null }) => {
  const { user } = useAuth()
  const [matches, setMatches] = useState([])
  const [selectedMatchKey, setSelectedMatchKey] = useState(() => initialMatchKey || null)
  const [conversationId, setConversationId] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [usage, setUsage] = useState(null)
  const [loadingMatches, setLoadingMatches] = useState(true)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [streaming, setStreaming] = useState(false)
  const [activity, setActivity] = useState('')
  const [activeTools, setActiveTools] = useState([])
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [error, setError] = useState('')
  const abortRef = useRef(null)
  const textareaRef = useRef(null)
  const conversationRef = useRef(null)
  const pendingScrollRef = useRef(null)

  const activeMatch = useMemo(
    () => matches.find((match) => match.key === selectedMatchKey) || null,
    [matches, selectedMatchKey]
  )

  const authenticatedFetch = async (path, options = {}) => {
    const token = await user.getIdToken()
    return fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
    })
  }

  useEffect(() => {
    let cancelled = false
    let intervalId
    const loadMatches = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/oracle-live/matches`)
        const data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(data.message || 'Não foi possível carregar os jogos live.')
        if (cancelled) return
        const nextMatches = Array.isArray(data.matches) ? data.matches : []
        setMatches(nextMatches)
        setSelectedMatchKey((current) => {
          if (nextMatches.some((match) => match.key === current)) return current
          if (initialMatchKey && nextMatches.some((match) => match.key === initialMatchKey)) return initialMatchKey
          return nextMatches[0]?.key || null
        })
        setError('')
      } catch (requestError) {
        if (!cancelled) {
          setMatches((current) => {
            if (!current.length) setError(requestError.message)
            return current
          })
        }
      } finally {
        if (!cancelled) setLoadingMatches(false)
      }
    }
    loadMatches()
    intervalId = window.setInterval(loadMatches, 15000)
    return () => {
      cancelled = true
      if (intervalId) window.clearInterval(intervalId)
    }
    // Polling intentionally owns its initial match selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMatchKey])

  useEffect(() => {
    if (!selectedMatchKey) {
      setConversationId(null)
      setMessages([])
      return undefined
    }
    let cancelled = false
    setConversationId(null)
    setMessages([])
    setError('')
    setSidebarOpen(false)
    pendingScrollRef.current = 'top'
    if (!user) return undefined

    setLoadingHistory(true)
    Promise.all([
      authenticatedFetch(`/api/oracle-live/conversations/match/${encodeURIComponent(selectedMatchKey)}`),
      authenticatedFetch('/api/oracle-live/usage'),
    ]).then(async ([historyResponse, usageResponse]) => {
      const historyData = await historyResponse.json().catch(() => ({}))
      const usageData = await usageResponse.json().catch(() => ({}))
      if (!historyResponse.ok) throw new Error(historyData.message || 'Não foi possível abrir o histórico deste jogo.')
      if (cancelled) return
      const restored = (historyData.turns || []).flatMap((turn) => [
        makeMessage('user', turn.question, { createdAt: turn.askedAt }),
        makeMessage('assistant', turn.answer || '', { createdAt: turn.completedAt, tools: turn.tools || [], polymarketIntent: turn.polymarketIntent || null }),
      ])
      setConversationId(historyData.conversation?._id || null)
      setMessages(restored)
      if (usageResponse.ok) setUsage(usageData.usage || null)
    }).catch((requestError) => {
      if (!cancelled) setError(requestError.message)
    }).finally(() => {
      if (!cancelled) setLoadingHistory(false)
    })
    return () => { cancelled = true }
    // authenticatedFetch only changes when the authenticated user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMatchKey, user])

  useEffect(() => {
    const mode = pendingScrollRef.current
    const conversation = conversationRef.current
    if (!mode || !conversation) return
    pendingScrollRef.current = null
    if (mode === 'top') {
      conversation.scrollTo({ top: 0, behavior: 'auto' })
      return
    }
    const userMessages = conversation.querySelectorAll('.oracle-message.is-user')
    const latestQuestion = userMessages[userMessages.length - 1]
    if (latestQuestion) {
      const conversationTop = conversation.getBoundingClientRect().top
      const questionTop = latestQuestion.getBoundingClientRect().top
      conversation.scrollTo({ top: Math.max(0, conversation.scrollTop + questionTop - conversationTop - 16), behavior: 'auto' })
    }
  }, [messages])

  useEffect(() => {
    if (!textareaRef.current) return
    textareaRef.current.style.height = 'auto'
    textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`
  }, [input])

  useEffect(() => () => abortRef.current?.abort(), [])

  const usagePercent = useMemo(() => {
    if (!usage?.user?.limit) return 0
    return Math.min(100, Math.round((usage.user.used / usage.user.limit) * 100))
  }, [usage])

  const selectMatch = (matchKey) => {
    if (streaming || matchKey === selectedMatchKey) return
    abortRef.current?.abort()
    setSelectedMatchKey(matchKey)
  }

  const updateStreamingMessage = (messageId, updater) => {
    setMessages((current) => current.map((message) => message.id === messageId ? updater(message) : message))
  }

  const submitPrompt = async (rawPrompt) => {
    const prompt = String(rawPrompt || '').trim()
    if (!prompt || streaming || !activeMatch) return
    if (!user) {
      setError('Inicia sessão para conversar com o Oráculo Live.')
      return
    }

    const assistantId = `assistant-${Date.now()}`
    pendingScrollRef.current = 'question'
    setMessages((current) => [...current, makeMessage('user', prompt), { id: assistantId, role: 'assistant', text: '', streaming: true, tools: [] }])
    setInput('')
    setError('')
    setStreaming(true)
    setActivity('A ler o estado atual do jogo…')
    setActiveTools([])
    const controller = new AbortController()
    abortRef.current = controller

    try {
      const response = await authenticatedFetch('/api/oracle-live/chat', {
        method: 'POST',
        body: JSON.stringify({ message: prompt, matchKey: activeMatch.key, conversationId }),
        signal: controller.signal,
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        throw new Error(data.message || 'O Oráculo Live não conseguiu responder.')
      }

      await parseSseStream(response.body, (type, data) => {
        if (type === 'ready') setConversationId(data.conversationId)
        if (type === 'status') setActivity(data.message || 'A analisar o jogo…')
        if (type === 'tool') {
          const label = TOOL_LABELS[data.name] || 'A consultar dados live'
          setActivity(data.state === 'completed' ? 'A organizar a resposta…' : `${label}…`)
          if (data.state === 'started') {
            setActiveTools((current) => current.includes(label) ? current : [...current, label])
            updateStreamingMessage(assistantId, (message) => ({ ...message, tools: message.tools.includes(data.name) ? message.tools : [...message.tools, data.name] }))
          }
        }
        if (type === 'delta') {
          setActivity('')
          updateStreamingMessage(assistantId, (message) => ({ ...message, text: message.text + (data.text || '') }))
        }
        if (type === 'done') {
          if (data.usage) setUsage(data.usage)
          updateStreamingMessage(assistantId, (message) => ({ ...message, streaming: false, mcpCallCount: data.mcpCallCount }))
          return false
        }
        if (type === 'error') throw new Error(data.message || 'A resposta live foi interrompida.')
        return true
      })
    } catch (requestError) {
      const stopped = requestError.name === 'AbortError'
      const message = stopped ? 'Resposta interrompida.' : requestError.message
      setError(stopped ? '' : message)
      updateStreamingMessage(assistantId, (current) => ({ ...current, streaming: false, failed: true, text: current.text || message }))
    } finally {
      setStreaming(false)
      setActivity('')
      setActiveTools([])
      abortRef.current = null
    }
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    submitPrompt(input)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      submitPrompt(input)
    }
  }

  return (
    <main className="oracle-page oracle-live-page">
      <button type="button" className={`oracle-sidebar-scrim ${sidebarOpen ? 'is-visible' : ''}`} aria-label="Fechar jogos live" onClick={() => setSidebarOpen(false)} />

      <aside className={`oracle-sidebar oracle-live-sidebar ${sidebarOpen ? 'is-open' : ''}`}>
        <div className="oracle-sidebar-top">
          <div className="oracle-brand-lockup">
            <span className="oracle-glyph"><IconifyIcon icon="solar:stars-minimalistic-bold" width="20" /></span>
            <div><strong>Oráculo Live</strong><small>Football intelligence</small></div>
          </div>
          <button className="oracle-icon-button mobile-only" type="button" onClick={() => setSidebarOpen(false)} aria-label="Fechar">
            <IconifyIcon icon="solar:close-circle-linear" width="22" />
          </button>
        </div>

        <div className="oracle-history-heading oracle-live-list-heading"><span>Jogos agora</span><small>{matches.length}</small></div>
        <div className="oracle-history-list oracle-live-match-list">
          {loadingMatches && !matches.length && <div className="oracle-history-loading">A sincronizar jogos live…</div>}
          {!loadingMatches && !matches.length && <div className="oracle-history-empty">Não existem jogos disponíveis neste momento.</div>}
          {matches.map((match) => (
            <button
              type="button"
              key={match.key}
              className={`oracle-live-match ${selectedMatchKey === match.key ? 'is-active' : ''}`}
              onClick={() => selectMatch(match.key)}
              disabled={streaming}
            >
              <span className="oracle-live-match-meta"><i />{match.league}<b>{minuteLabel(match)}</b></span>
              <span className="oracle-live-team"><span>{match.home}</span><strong>{match.homeScore}</strong></span>
              <span className="oracle-live-team"><span>{match.away}</span><strong>{match.awayScore}</strong></span>
            </button>
          ))}
        </div>

        <div className="oracle-sidebar-footer">
          <div className="oracle-quota-row">
            <span className="oracle-quota-ring" style={{ '--usage': `${usagePercent * 3.6}deg` }}><i /></span>
            <div><span>Uso diário partilhado</span><strong>{usage ? `${usage.user.remaining} de ${usage.user.limit}` : (user ? 'A calcular…' : 'Inicia sessão')}</strong></div>
          </div>
        </div>
      </aside>

      <section className="oracle-workspace">
        <header className="oracle-workspace-header">
          <button className="oracle-icon-button mobile-only" type="button" onClick={() => setSidebarOpen(true)} aria-label="Abrir jogos live">
            <IconifyIcon icon="solar:hamburger-menu-linear" width="22" />
          </button>
          <div className="oracle-context-title">
            <span>{activeMatch ? `${activeMatch.home} – ${activeMatch.away}` : 'Oráculo Live'}</span>
            <small>{activeMatch ? `${activeMatch.league} · ${activeMatch.homeScore}–${activeMatch.awayScore} · ${minuteLabel(activeMatch)}` : 'Seleciona um jogo a decorrer'}</small>
          </div>
          <div className="oracle-coverage-pill"><i /> Em direto</div>
        </header>

        <div ref={conversationRef} className={`oracle-conversation ${messages.length ? 'has-messages' : 'is-empty'}`}>
          {!messages.length ? (
            <section className="oracle-welcome oracle-live-welcome">
              <div className="oracle-welcome-mark"><IconifyIcon icon="solar:stars-minimalistic-bold-duotone" width="30" /></div>
              <span className="oracle-kicker">PERGUNTA AO JOGO</span>
              <h1>{activeMatch ? <>Vê o jogo a acontecer.<br />Pergunta o que mudou.</> : <>Os jogos live<br />aparecem aqui.</>}</h1>
              <p>{activeMatch ? `Conversa sobre ${activeMatch.home} – ${activeMatch.away}: resultado, estatísticas, acontecimentos, remates, xG e odds disponíveis.` : 'Quando existir um jogo a decorrer, podes selecioná-lo à esquerda e conversar com os dados em tempo real.'}</p>
              {activeMatch && (
                <div className="oracle-starter-grid">
                  {STARTERS.map((starter) => (
                    <button type="button" key={starter.title} onClick={() => submitPrompt(starter.prompt)} disabled={!user}>
                      <span><IconifyIcon icon={starter.icon} width="21" /></span>
                      <strong>{starter.title}</strong>
                      <p>{starter.prompt}</p>
                      <IconifyIcon className="oracle-starter-arrow" icon="solar:arrow-right-linear" width="18" />
                    </button>
                  ))}
                </div>
              )}
            </section>
          ) : (
            <div className="oracle-message-list" aria-live="polite">
              {messages.map((message) => (
                <article className={`oracle-message is-${message.role} ${message.failed ? 'is-failed' : ''}`} key={message.id}>
                  <div className="oracle-message-avatar">
                    {message.role === 'assistant' ? <IconifyIcon icon="solar:stars-minimalistic-bold" width="18" /> : (user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="oracle-message-content">
                    <div className="oracle-message-label">{message.role === 'assistant' ? 'Oráculo Live' : 'Tu'}</div>
                    {message.text ? <div className="oracle-markdown"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: MarkdownLink }}>{message.text}</ReactMarkdown></div> : message.streaming ? <div className="oracle-thinking"><i /><i /><i /></div> : null}
                    {message.role === 'assistant' && message.text && !message.streaming && (
                      <button className="oracle-copy" type="button" onClick={() => navigator.clipboard?.writeText(message.text)} aria-label="Copiar resposta">
                        <IconifyIcon icon="solar:copy-linear" width="16" /> Copiar
                      </button>
                    )}
                  </div>
                </article>
              ))}
              {streaming && activity && <div className="oracle-activity"><span><i /></span><strong>{activity}</strong>{activeTools.length > 1 && <small>{activeTools.length} consultas</small>}</div>}
              <div />
            </div>
          )}
        </div>

        <div className="oracle-composer-zone">
          {error && <div className="oracle-error"><IconifyIcon icon="solar:danger-triangle-linear" width="18" />{error}</div>}
          {!user ? (
            <div className="oracle-live-auth">
              <span>Inicia sessão para conversar com os jogos em direto.</span>
              <Link to="/login" state={{ from: `/live?view=oracle-live${selectedMatchKey ? `&match=${encodeURIComponent(selectedMatchKey)}` : ''}` }}>Iniciar sessão</Link>
            </div>
          ) : (
            <form className="oracle-composer" onSubmit={handleSubmit}>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(event) => setInput(event.target.value.slice(0, 2000))}
                onKeyDown={handleKeyDown}
                placeholder={activeMatch ? `Pergunta sobre ${activeMatch.home} – ${activeMatch.away}…` : 'Seleciona primeiro um jogo live…'}
                rows="1"
                disabled={streaming || !activeMatch || loadingHistory}
                aria-label="Pergunta ao Oráculo Live"
              />
              <div className="oracle-composer-footer">
                <div><IconifyIcon icon="solar:translation-2-linear" width="15" /> Dados live verificados</div>
                {streaming ? (
                  <button type="button" className="oracle-stop-button" onClick={() => abortRef.current?.abort()} aria-label="Parar resposta"><span /></button>
                ) : (
                  <button type="submit" className="oracle-send-button" disabled={!input.trim() || !activeMatch || loadingHistory} aria-label="Enviar pergunta"><IconifyIcon icon="solar:arrow-up-linear" width="20" /></button>
                )}
              </div>
            </form>
          )}
          <p className="oracle-disclaimer">Os dados live podem sofrer correções. Confirma sempre valores importantes.</p>
        </div>
      </section>
    </main>
  )
}

export default OracleLive
